import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import {
  Grid,
  Paper,
  Stack,
  Select,
  MenuItem,
  SelectChangeEvent,
  Link,
  useTheme,
  alpha,
} from '@mui/material'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { makeStyles } from 'tss-react/mui'
import { ReactComponent as INESLogo } from '../../assets/logos/Suite.svg'
import { ReactComponent as INESWhiteLogo } from '../../assets/logos/Ines_white.svg'
import AuthBG from '../../assets/auth_bg.svg'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { changeCurrentLanguage } from '../../store/actions'

const useStyles = makeStyles()((theme) => ({
  root: {
    backgroundImage: 'linear-gradient(348deg, rgba(252,233,204,1) 40%, rgba(255,255,255,1) 100%)',
  },
  paper: {
    padding: '24px',
    width: '450px',
    borderRadius: 7,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    backgroundColor: alpha(theme.palette.background.default, 0.8),
    backdropFilter: 'blur(20px)',
  },
  image: {
    width: 350,
    position: 'absolute',
    top: 10,
    left: 10,
    cursor: 'pointer',
  },
  webbutton: {
    width: 200,
    position: 'absolute',
    bottom: 15,
    left: 0,
  },
}))

const FsAuthWrapper = ({ children }: { children: React.ReactNode }) => {
  const { classes } = useStyles()
  const { currentLanguage } = useAppSelector((state) => state.currentUser)
  const dispatch = useAppDispatch()
  const { i18n } = useTranslation()
  const navigate = useNavigate()
  const theme = useTheme()
  const location = useLocation()

  const switchLanguage = (event: SelectChangeEvent) => {
    const l = event.target.value as 'de' | 'en'
    i18n.changeLanguage(l)
    document.documentElement.setAttribute('lang', l)
    dispatch(changeCurrentLanguage(l))
  }

  useEffect(() => {
    dispatch(changeCurrentLanguage(i18n.language as 'de' | 'en'))
  }, [])

  return (
    <div className={classes.root}>
      <Box
        minWidth="100%"
        height="100vh"
        position="absolute"
        top={0}
        left={0}
        sx={{
          backgroundImage: `url(${AuthBG})`,
          backgroundRepeat: 'no-repeat',
          backgroundPositionY: 'bottom',
          backgroundPositionX: 'center',
          backgroundSize: '100%',
          opacity: 0.9,
        }}
      />
      <Box
        className={classes.image}
        mt={4}
        ml={4}
        onClick={() => navigate('/login')}
        sx={{ pointerEvents: location.pathname === '/login' ? 'none' : '' }}
      >
        <INESLogo width={200} height={75} />
      </Box>
      <Link
        target="_blank"
        href={`https://www.ines-analytics.com/${currentLanguage === 'en' ? 'en' : ''}`}
      >
        <Box className={classes.webbutton} mb={4} ml={4}>
          <INESWhiteLogo width={200} height={50} />
        </Box>
      </Link>

      <Stack direction="row" justifyContent="center">
        <Grid
          container
          spacing={0}
          direction="column"
          alignItems="center"
          justifyContent="center"
          style={{
            minHeight: '100vh',
            maxWidth: '700px',
          }}
        >
          <Grid zIndex={20}>
            <Paper className={classes.paper} elevation={10} square>
              <Stack
                direction="row"
                justifyContent="flex-end"
                width="100%"
                position="absolute"
                top={20}
                right={20}
              >
                <Select
                  id="lang-select"
                  value={currentLanguage}
                  onChange={switchLanguage}
                  sx={{ height: 24 }}
                >
                  <MenuItem value="de" key="lang-de" dense>
                    <Typography variant="body2">DE</Typography>
                  </MenuItem>
                  <MenuItem value="en" key="lang-en" dense>
                    <Typography variant="body2">ENG</Typography>
                  </MenuItem>
                </Select>
              </Stack>
              {children}
            </Paper>
          </Grid>
        </Grid>
      </Stack>
    </div>
  )
}

export default FsAuthWrapper
